<script setup lang="ts">
/**
 * The documents sidebar's header: its selection bar, its title and its breadcrumb (MAR-948 — split
 * out of `FileExplorer.vue`, which imports it explicitly).
 *
 * Draft 05 — the bar swaps. The header IS this panel's selection bar, so the multi-delete question
 * takes it over rather than opening anything: `InlineConfirm`'s default strip is already a
 * full-width question with confirm and cancel, which is this row's shape exactly. The strip is armed
 * from the parent through the exposed `arm`, so the keyboard path asks the same question as the
 * mouse one (GitHub #50).
 */
import { X, ChevronRight, ArrowLeft, PanelRightClose, Trash2, TableProperties } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { useDocumentsStore } from '~/stores/documents'

defineProps<{
  /** The pending multi-delete's question, and what confirming it runs. */
  deleteQuestion: string
  deleteAction: () => Promise<void>
  /** A sheet is made of documents: offered only when the selection holds one (MAR-480, MAR-957). */
  canCreateSheet: boolean
  creatingSheet: boolean
  deleting: boolean
  /** The crumb an internal drag is hovering, highlighted as a drop target. */
  dropTarget: string | null
}>()

const emit = defineEmits<{
  'cancel-delete': []
  'create-sheet': []
  'delete-selected': []
  'crumb-dragover': [event: DragEvent, path: string]
  'crumb-dragleave': [event: DragEvent]
  'crumb-drop': [event: DragEvent, path: string]
}>()

const { t } = useI18n()
const { toggleDocsDrawer } = usePanelLayout()
const documents = useDocumentsStore()
const { selectedIds, currentPath, isFolderMode, breadcrumb } = storeToRefs(documents)
const { navigateTo, clearSelection } = documents

const confirm = ref<{ arm: () => void } | null>(null)
defineExpose({ arm: () => confirm.value?.arm() })

function goUp() {
  const crumbs = breadcrumb.value
  navigateTo(crumbs.length > 1 ? crumbs[crumbs.length - 2]!.path : '/')
}
</script>

<template>
  <!-- Fixed height (--header-h), shared with the PageContent header bar. -->
  <div class="h-(--header-h) px-3 border-b shrink-0 flex items-center">
    <InlineConfirm
      ref="confirm"
      :question="deleteQuestion"
      :action="deleteAction"
      @cancel="emit('cancel-delete')"
    >
      <div class="flex items-center justify-between w-full">
        <div v-if="selectedIds.size > 0" class="flex items-center">
          <button
            type="button"
            class="inline-flex items-center gap-1 h-5 pl-1.5 pr-1 rounded-full bg-muted text-xs text-muted-foreground hover:bg-accent-lighter transition-colors"
            :title="t('common.clear')"
            @click="clearSelection"
          >
            {{ selectedIds.size }}
            <X class="w-3 h-3" />
          </button>
        </div>
        <div v-else class="flex items-center gap-2">
          <button
            v-if="currentPath !== '/' && isFolderMode"
            type="button"
            class="text-muted-foreground hover:text-foreground transition-colors"
            @click="goUp"
          >
            <ArrowLeft class="h-4 w-4" />
          </button>
          <span
            class="text-sm font-medium rounded-md px-1 -mx-1 transition-colors"
            :class="dropTarget === '/' && 'bg-primary/20 text-primary'"
            @dragover="emit('crumb-dragover', $event, '/')"
            @dragleave="emit('crumb-dragleave', $event)"
            @drop="emit('crumb-drop', $event, '/')"
          >{{ t('nav.documents') }}</span>
        </div>
        <div class="flex items-center gap-0.5">
          <template v-if="selectedIds.size > 0">
            <button
              v-if="canCreateSheet"
              type="button"
              class="p-1 rounded-md text-primary hover:bg-primary-light transition-colors disabled:opacity-50"
              :title="t('common.newSheet')"
              :disabled="creatingSheet"
              @click="emit('create-sheet')"
            >
              <TableProperties class="w-4 h-4" />
            </button>
            <button
              type="button"
              class="p-1 rounded-md text-destructive hover:bg-destructive/10 transition-colors"
              :title="t('common.delete')"
              :disabled="deleting"
              :class="deleting && 'opacity-50 cursor-not-allowed'"
              @click="emit('delete-selected')"
            >
              <Trash2 class="w-4 h-4" />
            </button>
          </template>
          <button
            type="button"
            class="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
            :title="t('common.close')"
            @click="toggleDocsDrawer"
          >
            <PanelRightClose class="h-4 w-4" />
          </button>
        </div>
      </div>
    </InlineConfirm>

    <!-- Breadcrumb -->
    <div v-if="breadcrumb.length > 0 && isFolderMode" class="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
      <button
        type="button"
        class="hover:text-foreground transition-colors rounded-md px-0.5"
        :class="dropTarget === '/' && 'bg-primary/20 text-primary'"
        @click="navigateTo('/')"
        @dragover="emit('crumb-dragover', $event, '/')"
        @dragleave="emit('crumb-dragleave', $event)"
        @drop="emit('crumb-drop', $event, '/')"
      >
        /
      </button>
      <template v-for="crumb in breadcrumb" :key="crumb.path">
        <ChevronRight class="h-3 w-3" />
        <button
          type="button"
          class="hover:text-foreground transition-colors truncate max-w-[80px] rounded-md px-0.5"
          :class="dropTarget === crumb.path && 'bg-primary/20 text-primary'"
          @click="navigateTo(crumb.path)"
          @dragover="emit('crumb-dragover', $event, crumb.path)"
          @dragleave="emit('crumb-dragleave', $event)"
          @drop="emit('crumb-drop', $event, crumb.path)"
        >
          {{ crumb.name }}
        </button>
      </template>
    </div>
  </div>
</template>
